import { useState } from "react";
import { Link } from "react-router-dom";
import { Gauge, ArrowRight, ArrowLeft, Mail, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ThemeToggle } from "@/components/site/ThemeToggle";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
      toast.success("Si el correo está registrado, recibirá el enlace.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#1A1A1F] flex items-center justify-center p-6" data-testid="forgot-page">
      <div className="fixed top-4 right-4 z-50">
        <ThemeToggle testId="forgot-theme-toggle" />
      </div>
      <div className="w-full max-w-md">
        <Link to="/login" className="inline-flex items-center gap-2 text-[#A1A1AA] hover:text-[#F59E0B] text-xs font-mono uppercase tracking-widest mb-8" data-testid="forgot-back-link">
          <ArrowLeft className="w-3 h-3" /> Volver a iniciar sesión
        </Link>
        <Link to="/" className="inline-flex items-center gap-3 mb-10">
          <div className="w-10 h-10 border border-[#F59E0B] flex items-center justify-center bg-[#F59E0B]/10">
            <Gauge className="w-5 h-5 text-[#F59E0B]" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-heading font-black uppercase text-base">SGM México</div>
            <div className="font-mono text-[10px] text-[#A1A1AA] tracking-widest uppercase">Recuperar acceso</div>
          </div>
        </Link>

        {sent ? (
          <div className="border border-emerald-400/40 bg-emerald-400/5 p-6" data-testid="forgot-success">
            <CheckCircle2 className="w-10 h-10 text-emerald-400 mb-3" />
            <h2 className="font-heading font-black uppercase text-2xl mb-2">Revise su correo</h2>
            <p className="text-[#D4D4D8] text-sm leading-relaxed">
              Si <span className="text-[#F59E0B] font-mono">{email}</span> está registrado en SGM México,
              recibirá un enlace para restablecer su contraseña. El enlace expira en 60 minutos.
            </p>
            <Link to="/login" className="btn-primary mt-6 w-full" data-testid="forgot-back-login">
              <ArrowLeft className="w-4 h-4" /> Volver a iniciar sesión
            </Link>
          </div>
        ) : (
          <>
            <div className="overline mb-3">// Recuperar contraseña</div>
            <h2 className="font-heading font-black uppercase text-3xl mb-2">¿Olvidó su contraseña?</h2>
            <p className="text-[#A1A1AA] mb-10">
              Ingrese su correo y le enviaremos un enlace seguro para restablecerla.
            </p>
            <form onSubmit={submit} className="space-y-5" data-testid="forgot-form">
              <div>
                <label className="overline block mb-2">Email</label>
                <input
                  required type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-base"
                  placeholder="usuario@empresa.com"
                  data-testid="forgot-email"
                  autoComplete="email"
                />
              </div>
              <button type="submit" disabled={loading} className="btn-primary w-full disabled:opacity-50" data-testid="forgot-submit">
                {loading ? "Enviando..." : "Enviar enlace"} <Mail className="w-4 h-4" />
              </button>
            </form>
            <div className="mt-8 pt-8 border-t border-[#2E2E33] text-sm text-[#A1A1AA]">
              ¿Recordó su contraseña?{" "}
              <Link to="/login" className="text-[#F59E0B] hover:underline font-semibold" data-testid="forgot-to-login">
                Iniciar sesión
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
